import { css } from '@emotion/react';
import {
  Box,
  Button,
  Flex,
  Select,
  TextInput,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { useCategoryMainQuery } from '../../../../queries/useCategoryMainQuery';
import {
  SUBSCRIPTION_COURSES_SORT,
  type SubscriptionCoursesSort,
} from '../../../../queries/useSubscriptionCoursesQuery';

interface SubscriptionCoursesFilterBarProps {
  keyword: string;
  categorySlug: string | null;
  sort: SubscriptionCoursesSort;
  onSubmitKeyword: (keyword: string) => void;
  onChangeCategory: (categorySlug: string | null) => void;
  onChangeSort: (sort: SubscriptionCoursesSort) => void;
}

export const SubscriptionCoursesFilterBar = ({
  keyword,
  categorySlug,
  sort,
  onSubmitKeyword,
  onChangeCategory,
  onChangeSort,
}: SubscriptionCoursesFilterBarProps) => {
  const { t } = useTranslation();
  const [draftKeyword, setDraftKeyword] = useState(keyword);
  const [appliedKeyword, setAppliedKeyword] = useState(keyword);
  const { data: category } = useCategoryMainQuery({ options: { staleTime: Infinity } });

  if (appliedKeyword !== keyword) {
    setAppliedKeyword(keyword);
    setDraftKeyword(keyword);
  }

  const sortOptions = [
    // 정확도는 키워드가 있을 때만 매길 수 있어 검색 결과에서만 고를 수 있게 한다
    ...(keyword === ''
      ? []
      : [
          {
            value: SUBSCRIPTION_COURSES_SORT.SEARCH,
            label: t('subscription_courses.sort.accuracy'),
          },
        ]),
    { value: SUBSCRIPTION_COURSES_SORT.POPULAR, label: t('subscription_courses.sort.popular') },
    { value: SUBSCRIPTION_COURSES_SORT.RECENT, label: t('subscription_courses.sort.recent') },
    { value: SUBSCRIPTION_COURSES_SORT.RATING, label: t('subscription_courses.sort.rating') },
  ];

  return (
    <Flex direction="column" gap={16} css={styleMinWidthReset}>
      <Flex gap={8} align="center" css={styleMinWidthReset}>
        <TextInput
          value={draftKeyword}
          onChange={(event) => setDraftKeyword(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSubmitKeyword(draftKeyword.trim());
            }
          }}
          placeholder={t('subscription_courses.search.placeholder')}
          size="md"
          radius="md"
          css={styleKeywordInput}
        />
        <Button size="md" radius="md" onClick={() => onSubmitKeyword(draftKeyword.trim())}>
          {t('subscription_courses.search.submit')}
        </Button>
      </Flex>
      <Flex gap={12} align="flex-start" justify="space-between" css={styleControlRow}>
        <Box css={styleCategoryList}>
          <CategoryButton
            label={t('subscription_courses.category.all')}
            isActive={categorySlug === null}
            onClick={() => onChangeCategory(null)}
          />
          {(category?.categories ?? []).map((item) => (
            <CategoryButton
              key={item.slug}
              label={item.title}
              isActive={categorySlug === item.slug}
              onClick={() => onChangeCategory(item.slug)}
            />
          ))}
        </Box>
        <Select
          data={sortOptions}
          value={sort}
          onChange={(value) => {
            if (value) {
              onChangeSort(value as SubscriptionCoursesSort);
            }
          }}
          allowDeselect={false}
          size="sm"
          w={120}
          css={styleSortSelect}
        />
      </Flex>
    </Flex>
  );
};

const CategoryButton = ({
  label,
  isActive,
  onClick,
}: { label: string; isActive: boolean; onClick: () => void }) => (
  <Button
    variant={isActive ? 'filled' : 'default'}
    size="sm"
    radius="xl"
    onClick={onClick}
    css={styleCategoryButton}
  >
    {label}
  </Button>
);

/* 카테고리 줄이 가로 스크롤 영역이라, flex 아이템의 min-width: auto 로 부모까지 늘어나는 것을 막는다 */
const styleMinWidthReset = css({
  minWidth: 0,
});

const styleKeywordInput = css({
  flex: 1,
  minWidth: 0,
});

const styleControlRow = css({
  minWidth: 0,
  [desktopFirstMediaQuery.tablet]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

const styleCategoryList = css({
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  minWidth: 0,
  maxWidth: '100%',
  // 카테고리가 열 개를 넘어 좁은 화면에서는 줄바꿈 대신 가로 스크롤로 흘린다
  [desktopFirstMediaQuery.tablet]: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    paddingBottom: 4,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
});

const styleCategoryButton = css({
  flexShrink: 0,
});

const styleSortSelect = css({
  flexShrink: 0,
});
